import type { ContractStatus } from './types';

export type KampanjeKanalId =
  | 'klassekampen'
  | 'aftenposten'
  | 'bokstuen'
  | 'ig-influencer'
  | 'tv2-morgen'
  | 'litteraturhuset';

export type KampanjeKategori = 'medie' | 'sosial' | 'nisje';

export type KampanjeEffekt = {
  salgsMultiplikator: number;
  attentionBoost: number;
  omdommeBoost: number;
};

export type KampanjeKanal = {
  id: KampanjeKanalId;
  navn: string;
  beskrivelse: string;
  pris: number;
  varighetDager: number;
  kategori: KampanjeKategori;
  effekt: KampanjeEffekt;
  sjangerTreff: Record<string, number>;
  bokStatusKrav?: ContractStatus[];
};

export const SJANGER_TREFF_DEFAULT = 1.0;

export const KAMPANJE_KANALER: ReadonlyArray<KampanjeKanal> = [
  {
    id: 'klassekampen',
    navn: 'Klassekampen-annonse',
    beskrivelse: 'Helsides-annonse i Klassekampens bok-bilag.',
    pris: 18_000,
    varighetDager: 14,
    kategori: 'medie',
    effekt: {
      salgsMultiplikator: 1.4,
      attentionBoost: 10,
      omdommeBoost: 3,
    },
    sjangerTreff: {
      LITERARY: 1.4,
      POETRY: 1.5,
      NONFICTION: 1.3,
      SAKPROSA: 1.3,
      KRIM: 0.6,
      FEELGOOD: 0.5,
      ROMANCE: 0.6,
      UNGDOMSBOK: 0.8,
      BARNEBOK: 0.9,
    },
  },
  {
    id: 'aftenposten',
    navn: 'Aftenposten-helgebilag',
    beskrivelse: 'Lørdagsbilaget — bred dekning, balansert tone.',
    pris: 35_000,
    varighetDager: 7,
    kategori: 'medie',
    effekt: {
      salgsMultiplikator: 1.6,
      attentionBoost: 15,
      omdommeBoost: 2,
    },
    sjangerTreff: {
      LITERARY: 1.1,
      POETRY: 1.0,
      NONFICTION: 1.1,
      SAKPROSA: 1.1,
      KRIM: 1.1,
      FEELGOOD: 1.0,
      ROMANCE: 1.0,
      UNGDOMSBOK: 1.0,
      BARNEBOK: 1.0,
    },
  },
  {
    id: 'bokstuen',
    navn: 'Bokstuen-podcast',
    beskrivelse: 'Intervju i Bokstuesendingen — dypt og smalt.',
    pris: 8_000,
    varighetDager: 21,
    kategori: 'nisje',
    effekt: {
      salgsMultiplikator: 1.3,
      attentionBoost: 8,
      omdommeBoost: 1,
    },
    sjangerTreff: {
      LITERARY: 1.4,
      POETRY: 1.4,
      NONFICTION: 1.3,
      SAKPROSA: 1.3,
      KRIM: 0.9,
      FEELGOOD: 0.8,
      ROMANCE: 0.8,
      UNGDOMSBOK: 0.9,
      BARNEBOK: 0.9,
    },
  },
  {
    id: 'ig-influencer',
    navn: 'Instagram-influencer',
    beskrivelse: 'Bokstagrammer med 80k følgere lager carousel-post.',
    pris: 25_000,
    varighetDager: 5,
    kategori: 'sosial',
    effekt: {
      salgsMultiplikator: 2.0,
      attentionBoost: 25,
      omdommeBoost: -1,
    },
    sjangerTreff: {
      FEELGOOD: 1.6,
      ROMANCE: 1.6,
      UNGDOMSBOK: 1.5,
      KRIM: 1.2,
      LITERARY: 0.9,
      NONFICTION: 0.9,
      SAKPROSA: 0.8,
      POETRY: 0.4,
      BARNEBOK: 1.1,
    },
  },
  {
    id: 'tv2-morgen',
    navn: 'TV2 God Morgen Norge',
    beskrivelse: 'Sofa-intervju på direkten — eksplosiv synlighet.',
    pris: 65_000,
    varighetDager: 3,
    kategori: 'medie',
    effekt: {
      salgsMultiplikator: 2.5,
      attentionBoost: 30,
      omdommeBoost: 1,
    },
    sjangerTreff: {
      FEELGOOD: 1.5,
      KRIM: 1.4,
      SAKPROSA: 1.4,
      NONFICTION: 1.3,
      ROMANCE: 1.2,
      UNGDOMSBOK: 1.1,
      LITERARY: 1.0,
      POETRY: 0.7,
      BARNEBOK: 1.1,
    },
  },
  {
    id: 'litteraturhuset',
    navn: 'Litteraturhuset-lansering',
    beskrivelse: 'Boklansering med samtale på Litteraturhuset i Oslo.',
    pris: 22_000,
    varighetDager: 1,
    kategori: 'nisje',
    effekt: {
      salgsMultiplikator: 1.5,
      attentionBoost: 12,
      omdommeBoost: 5,
    },
    sjangerTreff: {
      LITERARY: 1.5,
      POETRY: 1.4,
      SAKPROSA: 1.4,
      NONFICTION: 1.3,
      KRIM: 1.0,
      FEELGOOD: 0.8,
      ROMANCE: 0.8,
      UNGDOMSBOK: 0.9,
      BARNEBOK: 0.9,
    },
  },
];

export function finnKanal(id: string): KampanjeKanal | undefined {
  return KAMPANJE_KANALER.find((k) => k.id === id);
}

export function sjangerTreffFor(kanal: KampanjeKanal, genre: string): number {
  const treff = kanal.sjangerTreff[genre];
  return typeof treff === 'number' ? treff : SJANGER_TREFF_DEFAULT;
}

export function treffEtikett(multiplikator: number): 'Lavt' | 'Middels' | 'Høyt' {
  if (multiplikator >= 1.3) return 'Høyt';
  if (multiplikator >= 0.9) return 'Middels';
  return 'Lavt';
}
